import { useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase/client'

export function useNotifications(recipientId: string | undefined) {
  return useQuery({
    queryKey: ['notifications', recipientId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('recipient_id', recipientId!)
        .order('created_at', { ascending: false })
        .limit(50)

      if (error) throw error
      return data
    },
    enabled: !!recipientId,
  })
}

export function useUnreadCount(recipientId: string | undefined) {
  return useQuery({
    queryKey: ['notifications', recipientId, 'unread-count'],
    queryFn: async () => {
      const { count, error } = await supabase
        .from('notifications')
        .select('*', { count: 'exact', head: true })
        .eq('recipient_id', recipientId!)
        .is('read_at', null)

      if (error) throw error
      return count ?? 0
    },
    enabled: !!recipientId,
  })
}

export function useMarkRead() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (notificationId: string) => {
      const { data, error } = await supabase
        .from('notifications')
        .update({ read_at: new Date().toISOString() })
        .eq('id', notificationId)
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ['notifications', data.recipient_id],
      })
    },
  })
}

export function useMarkAllRead(recipientId: string | undefined) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      if (!recipientId) throw new Error('Recipient ID is required')

      const { error } = await supabase
        .from('notifications')
        .update({ read_at: new Date().toISOString() })
        .eq('recipient_id', recipientId)
        .is('read_at', null)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['notifications', recipientId],
      })
    },
  })
}

export function useRealtimeNotifications(recipientId: string | undefined) {
  const queryClient = useQueryClient()

  useEffect(() => {
    if (!recipientId) return

    const channel = supabase
      .channel(`notifications:${recipientId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `recipient_id=eq.${recipientId}`,
        },
        () => {
          queryClient.invalidateQueries({
            queryKey: ['notifications', recipientId],
          })
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [recipientId, queryClient])
}
